import { useState, useEffect, useRef } from "react"
import { useQueryState } from "nuqs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown } from "@fortawesome/free-solid-svg-icons"
import Dropdown from "../../components/Dropdown"
import { useRadPeerReports } from "../../hooks/useRadPeer"
import ApiError from "../../components/ApiError"
import Loader from "../../components/Loader"
import { getDateRange, formatISODate } from "../../utils/datetime"

// RadPeer timestamps are stored as wall-clock datetimes; render them as-is
// (UTC) so the UI matches the values in the database, without a local shift.
const formatUtc = (value) =>
  formatISODate(value, undefined, { zone: "utc" }) || "—"

const clean = (value) => {
  if (value === null || value === undefined) return null
  const s = String(value).trim()
  if (s === "" || s.toLowerCase() === "null" || s.toLowerCase() === "undefined")
    return null
  return value
}

const yesNo = (value) => {
  const c = clean(value)
  if (c === null) return "—"
  const s = String(c).toLowerCase().trim()
  if (["true", "1", "yes", "y", "t"].includes(s)) return "Yes"
  if (["false", "0", "no", "n", "f"].includes(s)) return "No"
  return String(c)
}

const getSentToAcrAt = (orus) => {
  const sent = orus
    .map((o) => o.SentToAcrAt)
    .filter(Boolean)
    .sort()
  return sent.length ? sent[sent.length - 1] : null
}

const getLogStatus = (log, orus) => {
  const hasNack = !!log.NackMsg
  const hasAcrError = orus.some((o) => o.IsACRError)
  if (hasNack) return { label: "HL7 NACK", tone: "error" }
  if (hasAcrError) return { label: "ACR Error", tone: "error" }
  if (log.DuplicateOfLogId) return { label: "Duplicate", tone: "duplicate" }
  if (orus.some((o) => o.SentToAcrAt))
    return { label: "Sent to ACR", tone: "sent" }
  return { label: "Pending", tone: "pending" }
}

const toneStyles = {
  sent: "bg-green-50 border border-green-200 text-green-700",
  pending: "bg-amber-50 border border-amber-300 text-amber-800",
  error: "bg-red-50 border border-red-200 text-red-700",
  duplicate: "bg-gray-100 border border-gray-300 text-gray-600",
}
const toneDot = {
  sent: "bg-green-500",
  pending: "bg-amber-500",
  error: "bg-red-500",
  duplicate: "bg-gray-400",
}

const RadPeerReports = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [activeSearchTerm, setActiveSearchTerm] = useQueryState("search", {
    defaultValue: "",
  })
  const [dateRange, setDateRange] = useQueryState("dateRange", {
    defaultValue: "Today",
  })
  const [status, setStatus] = useQueryState("status", {
    defaultValue: "All",
  })
  const [expandedRow, setExpandedRow] = useState(null)

  const sentinelRef = useRef(null)
  const expandedRowRef = useRef(null)

  const toggleRowExpansion = (rowId) => {
    setExpandedRow((prev) => (prev === rowId ? null : rowId))
  }

  useEffect(() => {
    if (expandedRow && expandedRowRef.current) {
      const scrollToRow = () => {
        expandedRowRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        })
      }
      setTimeout(scrollToRow, 100)
      setTimeout(scrollToRow, 300)
    }
  }, [expandedRow])

  const getDateFilterValue = (range) => {
    if (range === "All") return undefined
    const interval = getDateRange(range)
    return [interval.start.toISODate(), interval.end.toISODate()].join(",")
  }

  const {
    data: { data, total } = { data: [], total: 0 },
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useRadPeerReports({
    limit: 20,
    sortBy: "ReceivedAt:DESC",
    filters: activeSearchTerm?.trim()
      ? { searchTerm: activeSearchTerm.trim() }
      : {
          timePeriod:
            dateRange === "All" ? undefined : getDateFilterValue(dateRange),
          status: status === "All" ? undefined : status.toLowerCase(),
        },
  })

  useEffect(() => {
    setExpandedRow(null)
  }, [activeSearchTerm, dateRange, status])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { rootMargin: "200px", threshold: 0.1 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const dateRangeOptions = [
    "Today",
    "Yesterday",
    "This Week",
    "Last Week",
    "This Month",
    "Last Month",
    "All",
  ]
  const statusOptions = ["All", "Sent", "Pending", "Error", "Duplicate"]

  const handleClearAll = () => {
    setDateRange("Today")
    setStatus("All")
  }

  const handleClearSearch = () => {
    setSearchTerm("")
    setActiveSearchTerm("")
  }

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-2">RadPeer Reports</h1>

      {/* Filter Row */}
      <div className="bg-gray-50 p-4 rounded-lg mb-2">
        <div className="flex gap-4">
          <div>
            <Dropdown
              disabled={!!activeSearchTerm}
              label="DATE RANGE"
              options={activeSearchTerm ? [""] : dateRangeOptions}
              setValue={setDateRange}
              title={
                activeSearchTerm
                  ? "Clear the search field to enable filters"
                  : "Filter by received date range"
              }
              value={activeSearchTerm ? "" : dateRange}
            />
          </div>

          <div>
            <Dropdown
              disabled={!!activeSearchTerm}
              label="STATUS"
              options={activeSearchTerm ? [""] : statusOptions}
              setValue={setStatus}
              title={
                activeSearchTerm
                  ? "Clear the search field to enable filters"
                  : "Filter by ACR submission status"
              }
              value={activeSearchTerm ? "" : status}
            />
          </div>

          <div className="flex items-end gap-2">
            <button
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
              disabled={!!activeSearchTerm}
              title={
                activeSearchTerm
                  ? "Clear the search field to enable filters"
                  : "Clear all filters"
              }
              onClick={handleClearAll}
            >
              Clear
            </button>
          </div>

          <div className="flex-1" />

          {/* Accession Number */}
          <div className="flex items-end gap-2">
            <div>
              <label className="block text-xs font-medium mb-1 ml-1 text-gray-700">
                ACCESSION NUMBER
              </label>
              <input
                className="w-full min-w-[300px] px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g. AN-2026-00142"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    setActiveSearchTerm(searchTerm)
                  }
                }}
              />
            </div>
            <button
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium disabled:cursor-not-allowed disabled:bg-blue-400"
              onClick={() => setActiveSearchTerm(searchTerm)}
            >
              Search
            </button>
            <button
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
              onClick={handleClearSearch}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="mt-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-20 gap-4">
            <Loader />
            <span className="text-gray-600">Loading RadPeer reports...</span>
          </div>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-gray-500">
                Showing <span className="font-bold">{data.length}</span> of{" "}
                <span className="font-bold">{total}</span> records
              </span>
            </div>

            {data.length > 0 ? (
              <div className="space-y-2 max-h-[calc(100vh-325px)] overflow-auto">
                {data.map((log) => {
                  const orus = log.oruList || []
                  const isExpanded = expandedRow === log.RadPeerLogId
                  const statusInfo = getLogStatus(log, orus)
                  const sentToAcrAt = getSentToAcrAt(orus)

                  return (
                    <div
                      key={log.RadPeerLogId}
                      ref={isExpanded ? expandedRowRef : null}
                      className={`bg-white border rounded-lg hover:shadow-sm transition-shadow overflow-hidden ${
                        isExpanded
                          ? "border-gray-400 shadow-sm border-2"
                          : "border-gray-200"
                      }`}
                    >
                      {/* Main Row */}
                      <div
                        className={`grid grid-cols-[16px_minmax(140px,1.4fr)_minmax(120px,1.2fr)_minmax(150px,1fr)_minmax(150px,1fr)_auto_20px] items-center gap-4 p-3 hover:cursor-pointer hover:bg-gray-100 ${
                          isExpanded ? "bg-gray-200" : ""
                        }`}
                        onClick={() => toggleRowExpansion(log.RadPeerLogId)}
                      >
                        <div
                          className={`w-2 h-2 rounded-full flex-shrink-0 ${toneDot[statusInfo.tone]}`}
                        />
                        <span className="font-semibold text-gray-900 truncate">
                          {log.AccessionNumber || "N/A"}
                        </span>
                        <span className="text-sm text-gray-700 truncate">
                          {clean(log.PatientIssuer) || "—"}
                        </span>
                        <span className="text-sm text-gray-500 tabular-nums">
                          {formatUtc(log.ReceivedAt)}
                        </span>
                        <span className="text-sm text-gray-500 tabular-nums">
                          {formatUtc(sentToAcrAt)}
                        </span>
                        <span
                          className={`px-3 py-1 text-xs rounded-full font-medium whitespace-nowrap ${toneStyles[statusInfo.tone]}`}
                        >
                          {statusInfo.label}
                        </span>
                        <FontAwesomeIcon
                          className={`w-4 h-4 text-gray-500 transition-transform ${
                            isExpanded ? "rotate-180" : ""
                          }`}
                          icon={faChevronDown}
                        />
                      </div>

                      {/* Accordion Content */}
                      {isExpanded && (
                        <div className="border-t border-gray-200 p-4 bg-gray-50 flex flex-col gap-4">
                          {log.NackMsg && (
                            <div className="bg-red-100 text-red-800 rounded-md p-3 text-sm">
                              <span className="font-semibold">NACK: </span>
                              {log.NackMsg}
                            </div>
                          )}

                          {log.DuplicateOfLogId && (
                            <div className="text-xs text-gray-500">
                              Duplicate of RadPeer log #{log.DuplicateOfLogId}
                            </div>
                          )}

                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                              ACR ORU Submissions ({orus.length})
                            </p>
                            {orus.length > 0 ? (
                              <div className="flex flex-col gap-3">
                                {orus.map((oru) => (
                                  <div
                                    key={oru.RadPeerOruId}
                                    className="rounded-md bg-white border border-gray-300 overflow-hidden"
                                  >
                                    <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-gray-100 border-b border-gray-200">
                                      {clean(oru.ScoreClass) && (
                                        <span className="px-2 py-0.5 text-xs rounded bg-indigo-50 border border-indigo-200 text-indigo-700 font-bold">
                                          RADPEER {clean(oru.ScoreClass)}
                                        </span>
                                      )}
                                      {clean(oru.ProcessFlag) && (
                                        <span className="px-2 py-0.5 text-xs rounded-full bg-gray-200 border border-gray-300 text-gray-700 font-medium">
                                          {clean(oru.ProcessFlag)}
                                        </span>
                                      )}
                                      {oru.IsACRError && (
                                        <span className="px-2 py-0.5 text-xs rounded-full bg-red-50 border border-red-200 text-red-700 font-medium">
                                          ACR Error
                                        </span>
                                      )}
                                      <span className="text-xs text-gray-500 ml-auto">
                                        {clean(oru.Modality) || "—"}
                                        {clean(oru.Reviewer)
                                          ? ` · ${clean(oru.Reviewer)} reviewing ${
                                              clean(oru.Reviewee) || "—"
                                            }`
                                          : ""}
                                      </span>
                                    </div>
                                    <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
                                      <Field
                                        label="Score / Class"
                                        value={
                                          [
                                            clean(oru.Score),
                                            clean(oru.ScoreClass),
                                          ]
                                            .filter(Boolean)
                                            .join(" · ") || "—"
                                        }
                                      />
                                      <Field
                                        label="Modality"
                                        value={clean(oru.Modality) || "—"}
                                      />
                                      <Field
                                        label="Reviewer"
                                        value={clean(oru.Reviewer) || "—"}
                                      />
                                      <Field
                                        label="Reviewee"
                                        value={clean(oru.Reviewee) || "—"}
                                      />
                                      <Field
                                        label="Significant"
                                        value={yesNo(oru.Significant)}
                                      />
                                      <Field
                                        label="Has Reviewed"
                                        value={yesNo(oru.HasReviewed)}
                                      />
                                      <Field
                                        label="Score Date"
                                        value={formatUtc(oru.ScoreDate)}
                                      />
                                      <Field
                                        label="Sent to ACR At"
                                        value={formatUtc(oru.SentToAcrAt)}
                                      />
                                      <Field
                                        label="Location"
                                        value={clean(oru.Location) || "—"}
                                      />
                                      <Field
                                        label="Process Flag"
                                        value={clean(oru.ProcessFlag) || "—"}
                                      />
                                      {clean(oru.Notes) && (
                                        <div className="col-span-2 md:col-span-4">
                                          <FieldLabel>Notes</FieldLabel>
                                          <p className="text-sm text-gray-700">
                                            {clean(oru.Notes)}
                                          </p>
                                        </div>
                                      )}
                                      {oru.IsACRError ? (
                                        <div className="col-span-2 md:col-span-4">
                                          <FieldLabel>
                                            ACR Error Description
                                          </FieldLabel>
                                          <p className="text-sm text-red-600 break-words">
                                            {clean(oru.AcrErrorDesc) || "N/A"}
                                          </p>
                                        </div>
                                      ) : (
                                        clean(oru.AcrResponseText) && (
                                          <div className="col-span-2 md:col-span-4">
                                            <FieldLabel>
                                              ACR Response
                                            </FieldLabel>
                                            <p className="text-sm text-gray-600 break-words">
                                              {clean(oru.AcrResponseText)}
                                            </p>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500 italic">
                                No ACR ORU submissions for this message.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}

                <div ref={sentinelRef} className="h-1" />

                {hasNextPage && (
                  <div className="flex justify-center py-4">
                    {isFetchingNextPage ? (
                      <div className="flex items-center gap-2">
                        <Loader />
                        <span className="text-gray-600">
                          Loading more records...
                        </span>
                      </div>
                    ) : (
                      <button
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors"
                        disabled={isFetchingNextPage}
                        onClick={fetchNextPage}
                      >
                        Load More
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No records found. Try adjusting your filters.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const FieldLabel = ({ children }) => (
  <span className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
    {children}
  </span>
)

const Field = ({ label, value }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <span className="text-sm text-gray-800">{value}</span>
  </div>
)

export default RadPeerReports
